import React, { useState, useEffect, useMemo } from "react";
import { type NodeProps, type Node, useReactFlow, useNodes, useEdges } from "@xyflow/react";
import { FileText, X, TableIcon, Copy, Code } from "lucide-react";
import syntaxHighlight from "../../utils/jsonViewer";
import { getAllAncestorNodes } from "../../utils/workflowHelpers";
import { AvailableVariable } from "../../common/ui/types";
import { RichTextEditor } from "../../../components/RichTextEditor";
import { WorkflowBaseNode } from "../../common/shared/WorkflowBaseNode";
import { WorkflowNodeModal } from "../../common/shared/WorkflowNodeModal";

export const PDFGeneratorNode: React.FC<NodeProps<Node<any>>> = ({
  id,
  data,
  selected,
}) => {
  const [pdfInputData, setPdfInputData] = useState<string>(
    (data?.pdfInputData as string) || "",
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [templateHtml, setTemplateHtml] = useState(
    (data?.pdfTemplate as string) || "<p></p>",
  );
  const { setNodes } = useReactFlow();
  const allFlowNodes = useNodes();
  const allFlowEdges = useEdges();

  const hasTemplate = !!data?.pdfTemplate && data.pdfTemplate !== "<p></p>";
  const hasInputData = !!pdfInputData;
  const isConfigured = hasTemplate || hasInputData;

  // Ancestor nodes (needed for previewVariables + auto-set pdfInputData)
  const previousNodes = useMemo(() => {
    const ancestors = getAllAncestorNodes(id, allFlowNodes, allFlowEdges);
    return ancestors.map((n) => ({
      nodeId: n.id,
      nodeName: n.data?.label || n.data?.name || n.id,
      nodeType: n.type || "unknown",
      inputs: n.data?.inputs || {},
      outputs: n.data?.outputs || {},
    })) as AvailableVariable[];
  }, [id, allFlowNodes, allFlowEdges]);

  // Auto-set input data from first ancestor
  useEffect(() => {
    const newInputData =
      previousNodes.length > 0 ? `${previousNodes[0].nodeName}.outputs` : "";
    if (newInputData !== pdfInputData) {
      setPdfInputData(newInputData);
    }
  }, [previousNodes, pdfInputData]);

  // Flatten variables for RichTextEditor preview
  const previewVariables = useMemo(() => {
    const vars: Record<string, any> = {};
    previousNodes.forEach((node) => {
      Object.entries(node.outputs).forEach(([key, value]) => {
        vars[`${node.nodeName}.outputs.${key}`] = value;
      });
      Object.entries(node.inputs).forEach(([key, value]) => {
        vars[`${node.nodeName}.inputs.${key}`] = value;
      });
    });
    return vars;
  }, [previousNodes]);

  const handleDropOnInput = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const variable = e.dataTransfer.getData("text/plain");
    if (variable && variable.endsWith(".outputs")) {
      setPdfInputData(variable);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Execution outputs
  const hasExecutionOutputs = data?.outputs && Object.keys(data.outputs).length > 0;
  const highlightedActualOutputs = useMemo(() => {
    if (hasExecutionOutputs) return syntaxHighlight(JSON.stringify(data.outputs, null, 2));
    return "";
  }, [data?.outputs, hasExecutionOutputs]);

  const handleSaveConfiguration = () => {
    if (!hasTemplate && !hasInputData) return;
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id !== id) return node;
        const nodeData: any = {
          ...node.data,
          pdfTemplate: templateHtml,
          pdfData: previewVariables,
          templateHtml: undefined,
        };
        if (!hasTemplate) {
          nodeData.pdfInputData = pdfInputData;
        } else {
          delete nodeData.pdfInputData;
        }
        return { ...node, data: nodeData };
      }),
    );
    setIsModalOpen(false);
  };

  return (
    <WorkflowBaseNode
      id={id}
      data={data}
      selected={selected}
      title="PDF Generator"
      icon={<FileText className="w-4 h-4 text-rose-600" />}
      nodeDocsType="PDF"
      themeColor="rose"
      configureLabel="Configure PDF Generator"
      isConfigured={isConfigured}
      hasSourceHandle={true}
      validationErrors={
        !isConfigured
          ? ["PDF input data or template is required"]
          : []
      }
      statusText={{
        running: "Generating...",
        success: "PDF Generated",
        error: "Generation Failed",
      }}
      onConfigure={() => setIsModalOpen(true)}
    >

      {/* Modal */}
      <WorkflowNodeModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        nodeId={id}
        title="PDF Generator - Template editor"
        icon={<FileText className="w-5 h-5 text-rose-600" />}
        themeColor="rose"
        nodeDocsType="PDF"
        validationStatus={{
          isValid: isConfigured,
          errors: !isConfigured ? ["PDF input data or template is required"] : [],
        }}
        nodeData={data}
        nodeIcon={<FileText className="w-6 h-6 text-gray-400" />}
        emptyOutputsText="No PDF generated yet"
        saveLabel="Save Configuration"
        onSave={handleSaveConfiguration}
        renderOutputsSidebar={() => (
          <aside className="w-[420px] shrink-0 bg-gray-50/50 flex flex-col h-full overflow-hidden">
            <div className="px-4 py-2 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Code className="w-3.5 h-3.5 text-gray-500" />
                <h3 className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                  Output
                </h3>
              </div>
              {hasExecutionOutputs && (
                <span className="text-[10px] font-medium px-2 py-0.5 rounded-full text-green-700 bg-green-100">
                  {Object.keys(data.outputs).length} fields
                </span>
              )}
            </div>
            <div className="flex-1 p-4 overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-200 [&::-webkit-scrollbar-thumb]:rounded-full">
              {hasExecutionOutputs ? (
                <div className="bg-white border border-gray-200 rounded-lg p-3 font-mono text-xs overflow-x-auto">
                  <pre
                    className="whitespace-pre-wrap break-words"
                    dangerouslySetInnerHTML={{ __html: highlightedActualOutputs }}
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-center px-6">
                  <div className="w-14 h-14 mb-3 bg-gray-100 rounded-xl flex items-center justify-center text-gray-300 border border-dashed border-gray-300">
                    <FileText className="w-6 h-6" />
                  </div>
                  <h4 className="text-xs font-semibold text-gray-700 mb-1">No PDF generated yet</h4>
                  <p className="text-[10px] text-gray-400">Run the workflow to see outputs</p>
                </div>
              )}
            </div>
          </aside>
        )}
      >
        {/* Center: PDF config */}
        <div className="flex flex-col h-full overflow-hidden">
          {/* Input Data Source bar */}
          <div className="shrink-0 bg-gray-50/80 px-4 py-3 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Data Source
              </label>
              <div
                onDrop={handleDropOnInput}
                onDragOver={handleDragOver}
                className={`flex-1 flex items-center gap-2 px-3 py-1.5 border-2 border-dashed rounded-lg transition-all ${
                  pdfInputData
                    ? "border-emerald-400 bg-emerald-50/50"
                    : "border-gray-300 bg-white hover:border-rose-300"
                }`}
              >
                {pdfInputData ? (
                  <div className="flex-1 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <div className="p-1 bg-emerald-100 rounded text-emerald-600">
                        <TableIcon className="w-3 h-3" />
                      </div>
                      <code className="text-xs text-emerald-900 font-mono font-semibold">
                        {pdfInputData}
                      </code>
                    </div>
                    <button
                      onClick={() => setPdfInputData("")}
                      className="text-gray-400 hover:text-red-600 p-1 hover:bg-red-50 rounded transition-all shrink-0"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <div className="flex-1 flex items-center justify-center gap-2 text-gray-400 py-1">
                    <Copy className="w-3.5 h-3.5" />
                    <span className="text-[11px] font-medium">Drag output object here</span>
                  </div>
                )}
              </div>
            </div>
            {!hasInputData && !hasTemplate && (
              <p className="text-xs text-red-500 mt-1.5 ml-[72px]">PDF input data source is required</p>
            )}
          </div>

          {/* RichTextEditor fills remaining space */}
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden bg-white">
            <RichTextEditor
              content={data.pdfTemplate}
              onChange={setTemplateHtml}
              variables={previewVariables}
            />
          </div>
        </div>
      </WorkflowNodeModal>
    </WorkflowBaseNode>
  );
};

export default PDFGeneratorNode;
